import type { GateIssue, GateReport } from '@/types';
import type { Finding } from './FindingCard';

/**
 * Maps a GateReport into the unified Finding shape used by FindingCard /
 * FindingsPanel. Backend returns three parallel arrays grouped by severity;
 * the UI flattens them and re-groups on render.
 */
export function mapReportToFindings(report: GateReport): Finding[] {
  return [
    ...report.blocking_issues.map((issue) => toFinding(issue, 'BLOCKING')),
    ...report.alerts.map((issue) => toFinding(issue, 'ALERT')),
    ...report.info_items.map((issue) => toFinding(issue, 'INFO')),
  ];
}

function toFinding(issue: GateIssue, severity: Finding['severity']): Finding {
  // Findings synthesised from upstream agent failures (category=agent_failure)
  // have no contract excerpt — labelling them GROUNDED would be misleading.
  // Same applies when the rule engine produced an issue with no evidence text.
  const isAgentFailure = issue.category === 'agent_failure';
  const hasEvidence = Boolean(issue.evidence && issue.evidence.trim().length > 0);
  const grounding: Finding['groundingStatus'] =
    isAgentFailure || !hasEvidence ? 'MANUAL_REVIEW' : 'GROUNDED';

  // Derive the source agent from the rule_id of synthetic failure issues
  // (e.g. "agent_failure_compliance" → "Compliance Agent") so the FindingCard
  // header doesn't always read "Gate Agent" for every failure.
  let sourceAgent = 'Gate Agent';
  if (isAgentFailure && issue.rule_id.startsWith('agent_failure_')) {
    const agent = issue.rule_id.slice('agent_failure_'.length);
    sourceAgent = agent ? `${agent[0].toUpperCase()}${agent.slice(1)} Agent` : sourceAgent;
  }

  return {
    id: issue.rule_id || `${severity.toLowerCase()}-${issue.rule_name}-${issue.message.slice(0, 32)}`,
    title: issue.rule_name || issue.rule_id || issue.message,
    description: issue.message,
    severity,
    sourceAgent,
    clauseReference: null,
    evidenceText: issue.evidence,
    kbSource: null,
    kbArticle: null,
    groundingStatus: grounding,
  };
}
